import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    GetBuildingFloorStack,
    GetFloorLayout,
    CreateFloorLayout,
    UpdateFloorLayout,
    PublishFloorLayout,
    DeleteFloorLayout,
    GetFloorLayoutOccupancy,
    GetPlaceableEntities,
} from '../services/backend';
import { Notify, NotifyError } from '../utils/functions';

export function useGetBuildingFloorStack(buildingId) {
    return useQuery(
        ['building-floor-stack', buildingId],
        () => GetBuildingFloorStack(buildingId),
        {
            enabled: !!buildingId,
            refetchOnWindowFocus: false,
            staleTime: 60 * 1000, // 1 minute — occupancy changes more often
            select: (data) => data?.data || null,
            onError: (error) => {
                NotifyError('Failed to fetch building floor stack', error);
                console.error('Error fetching building floor stack:', error);
            },
        }
    );
}

export function useGetFloorLayout(floorId, params = {}) {
    return useQuery(
        ['floor-layout', floorId, params],
        () => GetFloorLayout(floorId, params),
        {
            enabled: !!floorId,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000,
            retry: (count, error) => error?.response?.status !== 404 && count < 2,
            select: (data) => data?.data || null,
            onError: (error) => {
                // 404 is expected when no layout exists yet
                if (error?.response?.status !== 404) {
                    NotifyError('Failed to fetch floor layout', error);
                    console.error('Error fetching floor layout:', error);
                }
            },
        }
    );
}

export function useGetFloorLayoutOccupancy(floorId) {
    return useQuery(
        ['floor-layout-occupancy', floorId],
        () => GetFloorLayoutOccupancy(floorId),
        {
            enabled: !!floorId,
            refetchOnWindowFocus: true,
            staleTime: 30 * 1000,
            refetchInterval: 60 * 1000,
            retry: (count, error) => error?.response?.status !== 404 && count < 2,
            select: (data) => data?.data || null,
            onError: (error) => {
                if (error?.response?.status !== 404) {
                    NotifyError('Failed to fetch floor occupancy', error);
                    console.error('Error fetching floor occupancy:', error);
                }
            },
        }
    );
}

export function useGetPlaceableEntities(floorId) {
    return useQuery(
        ['floor-layout-placeable', floorId],
        () => GetPlaceableEntities(floorId),
        {
            enabled: !!floorId,
            refetchOnWindowFocus: false,
            staleTime: 60 * 1000,
            select: (data) => data?.data || null,
            onError: (error) => {
                NotifyError('Failed to fetch placeable entities', error);
                console.error('Error fetching placeable entities:', error);
            },
        }
    );
}

export function useCreateFloorLayout() {
    const queryClient = useQueryClient();
    return useMutation(({ floorId, data }) => CreateFloorLayout(floorId, data), {
        onSuccess: (_data, variables) => {
            Notify('Floor layout created successfully');
            queryClient.invalidateQueries(['floor-layout', variables.floorId]);
            queryClient.invalidateQueries(['floor-layout-placeable', variables.floorId]);
            queryClient.invalidateQueries(['building-floor-stack']);
        },
        onError: (error) => {
            NotifyError('Failed to create floor layout', error);
            console.error('Error creating floor layout:', error);
        },
    });
}

export function useUpdateFloorLayout() {
    const queryClient = useQueryClient();
    return useMutation(({ floorId, data }) => UpdateFloorLayout(floorId, data), {
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries(['floor-layout', variables.floorId]);
            queryClient.invalidateQueries(['floor-layout-placeable', variables.floorId]);
        },
        onError: (error) => {
            NotifyError('Failed to save floor layout', error);
            console.error('Error updating floor layout:', error);
        },
    });
}

/** Explicit save with user-facing success toast (vs silent autosave). */
export function useSaveFloorLayout() {
    const queryClient = useQueryClient();
    return useMutation(({ floorId, data }) => UpdateFloorLayout(floorId, data), {
        onSuccess: (_data, variables) => {
            Notify('Floor layout saved');
            queryClient.invalidateQueries(['floor-layout', variables.floorId]);
            queryClient.invalidateQueries(['floor-layout-placeable', variables.floorId]);
        },
        onError: (error) => {
            NotifyError('Failed to save floor layout', error);
            console.error('Error saving floor layout:', error);
        },
    });
}

export function usePublishFloorLayout() {
    const queryClient = useQueryClient();
    return useMutation(({ floorId }) => PublishFloorLayout(floorId), {
        onSuccess: (_data, variables) => {
            Notify('Floor layout published successfully');
            queryClient.invalidateQueries(['floor-layout', variables.floorId]);
            queryClient.invalidateQueries(['floor-layout-occupancy', variables.floorId]);
            queryClient.invalidateQueries(['building-floor-stack']);
        },
        onError: (error) => {
            NotifyError('Failed to publish floor layout', error);
            console.error('Error publishing floor layout:', error);
        },
    });
}

export function useDeleteFloorLayout() {
    const queryClient = useQueryClient();
    return useMutation(({ floorId }) => DeleteFloorLayout(floorId), {
        onSuccess: (_data, variables) => {
            Notify('Floor layout deleted successfully');
            queryClient.invalidateQueries(['floor-layout', variables.floorId]);
            queryClient.invalidateQueries(['floor-layout-occupancy', variables.floorId]);
            queryClient.invalidateQueries(['floor-layout-placeable', variables.floorId]);
            queryClient.invalidateQueries(['building-floor-stack']);
        },
        onError: (error) => {
            NotifyError('Failed to delete floor layout', error);
            console.error('Error deleting floor layout:', error);
        },
    });
}
